import React, { useRef } from 'react';
import {
  CheckCircle, Download, ExternalLink, AlertTriangle,
  FileText, ArrowRight, Filter, RefreshCw, Expand, Minimize2,
  HelpCircle, TrendingUp, MessageSquare, ChevronDown, ChevronUp,
  Users, Upload, X as XIcon, Lightbulb
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

/* ─── Types ────────────────────────────────────────────────────────────── */

interface CleaningIssue {
  type: string;
  severity: string;
  column: string;
  description: string;
  count: number;
  action: string;
  status?: string;
}

interface StakeholderQuestion {
  question: string;
  context: string;
  affectedColumns: string[];
  priority: string;
}

interface ChangeLogEntry {
  operation: string;
  details: string;
  rowsAffected: number;
}

interface CanonicalMappingEntry {
  original: string;
  canonical: string;
  confidence: number;
  reason: string;
  sourceColumn?: string;
}

interface CleanResult {
  summary: string;
  issuesFound: CleaningIssue[];
  stakeholderQuestions?: StakeholderQuestion[];
  canonicalMapping?: CanonicalMappingEntry[];
  smeDerivedInsights?: string[];
  smeFilesUsed?: string[];
  smeNotesProvided?: boolean;
  changeLog?: ChangeLogEntry[];
  cleaningLog: string;
  previewBefore: string;
  previewAfter: string;
  stats: Record<string, any>;
  cleanedFileUrl: string | null;
  cleanedFileName: string;
  originalFileNames: string[];
  pythonCode: string;
  sessionId: number | null;
}

interface CleaningOptions {
  removeDuplicates: boolean;
  handleMissing: boolean;
  standardizeDates: boolean;
  normalizeText: boolean;
  standardizeNumbers: boolean;
  forwardFill: boolean;
  removeOutliers: boolean;
}

type CombineMode = 'none' | 'union' | 'join';

interface DataCleaningInputProps {
  cleaningOptions: CleaningOptions;
  setCleaningOptions: (opts: CleaningOptions) => void;
  reconciliationKey: string;
  setReconciliationKey: (key: string) => void;
  fileCount: number;
  combineMode?: CombineMode;
  setCombineMode?: (mode: CombineMode) => void;
  smeNotes?: string;
  setSmeNotes?: (v: string) => void;
  smeFiles?: File[];
  setSmeFiles?: (files: File[]) => void;
}

interface DataCleaningResultsProps {
  result: CleanResult;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onSendToSmartAnalyst: () => void;
  onSendToCouncil: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

/* ─── Constants ────────────────────────────────────────────────────────── */

const OPTION_LABELS: Record<string, { label: string; desc: string }> = {
  removeDuplicates: { label: 'Remove Duplicates', desc: 'Detect and remove duplicate rows' },
  handleMissing: { label: 'Handle Missing Values', desc: 'Fill nulls with median/mode or drop' },
  standardizeDates: { label: 'Standardize Dates', desc: 'Normalize to YYYY-MM-DD format' },
  normalizeText: { label: 'Normalize Text', desc: 'Trim whitespace, consistent casing' },
  standardizeNumbers: { label: 'Standardize Numbers', desc: 'Fix currency symbols, decimal separators' },
  forwardFill: { label: 'Forward-Fill Rows', desc: 'Carry the last valid value down to fill gaps (ffill)' },
  removeOutliers: { label: 'Remove Outliers', desc: 'Flag/remove statistical outliers (IQR)' },
};

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const PRIORITY_COLOR: Record<string, string> = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low: 'bg-blue-50 border-blue-200 text-blue-700',
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const renderInline = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={j} className="font-semibold text-gray-800">{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={j}>{part.slice(1, -1)}</em>;
    return <span key={j}>{part}</span>;
  });
};

/* ─── Quality Score Ring ───────────────────────────────────────────────── */

const QualityRing: React.FC<{ score: number; label: string; color: string }> = ({ score, label, color }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="88" height="88" viewBox="0 0 88 88">
        <circle cx="44" cy="44" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <circle
          cx="44" cy="44" r={radius} fill="none"
          stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform="rotate(-90 44 44)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="44" y="40" textAnchor="middle" className="text-lg font-bold" fill={color} fontSize="18">{score}%</text>
        <text x="44" y="56" textAnchor="middle" fill="#94a3b8" fontSize="9">QUALITY</text>
      </svg>
      <span className="text-xs font-semibold text-gray-500">{label}</span>
    </div>
  );
};

/* ─── DataCleaningInput ────────────────────────────────────────────────── */

export const DataCleaningInput: React.FC<DataCleaningInputProps> = ({
  cleaningOptions, setCleaningOptions, reconciliationKey, setReconciliationKey, fileCount,
  combineMode, setCombineMode,
  smeNotes, setSmeNotes, smeFiles, setSmeFiles,
}) => {
  const smeFileInputRef = useRef<HTMLInputElement>(null);
  const handleSmeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!setSmeFiles) return;
    const picked = Array.from(e.target.files || []);
    setSmeFiles([...(smeFiles || []), ...picked].slice(0, 5));
    if (smeFileInputRef.current) smeFileInputRef.current.value = '';
  };
  const removeSmeFile = (index: number) => {
    if (!setSmeFiles || !smeFiles) return;
    setSmeFiles(smeFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-teal-200 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Filter className="w-4 h-4 text-teal-600" />
          Cleaning Operations
        </h4>
        <div className="space-y-2">
          {Object.entries(OPTION_LABELS).map(([key, { label, desc }]) => (
            <label key={key} className="flex items-start gap-3 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={(cleaningOptions as any)[key]}
                onChange={() => setCleaningOptions({ ...cleaningOptions, [key]: !(cleaningOptions as any)[key] })}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">{label}</span>
                <p className="text-xs text-gray-400">{desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      {fileCount > 1 && (
        <div className="bg-white border border-teal-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-teal-600" />
            Combine Files
          </h4>
          <p className="text-xs text-gray-500 mb-2">{fileCount} files uploaded — optionally merge them into one output file.</p>
          <select
            value={combineMode || 'none'}
            onChange={(e) => setCombineMode?.(e.target.value as CombineMode)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
          >
            <option value="none">Keep separate — clean each file on its own</option>
            <option value="union">Union — stack all rows into one file</option>
            <option value="join">Match on key — join files (VLOOKUP / XLOOKUP)</option>
          </select>
          {(combineMode || 'none') === 'join' && (
            <input
              type="text"
              value={reconciliationKey}
              onChange={(e) => setReconciliationKey(e.target.value)}
              placeholder="Key column to match on, e.g., Employee ID, Invoice Number"
              className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          )}
        </div>
      )}

      {/* SME Enrichment — optional subject-matter-expert context */}
      {(setSmeNotes || setSmeFiles) && (
        <div className="bg-white border border-teal-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-600" />
            SME Enrichment <span className="text-xs font-normal text-gray-400">(optional)</span>
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Paste interview notes, emails, or upload a document describing what the systems,
            applications, or values in your data actually are. The AI will use this context to
            propose canonical names and flag SME-backed issues.
          </p>

          {setSmeNotes && (
            <textarea
              value={smeNotes || ''}
              onChange={(e) => setSmeNotes(e.target.value)}
              rows={4}
              placeholder="e.g. 'Infosys' and 'INFOSYS LTD' both refer to the same vendor. 'FIN_REP' is the legacy financial reporting system, decommissioned in 2023..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none mb-3"
            />
          )}

          {setSmeFiles && (
            <>
              <input
                ref={smeFileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.json,.csv,.pdf,.docx,.doc"
                onChange={handleSmeFileChange}
                className="hidden"
                id="sme-file-input"
              />
              <label
                htmlFor="sme-file-input"
                className="flex items-center gap-2 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg px-3 py-2 cursor-pointer w-fit"
              >
                <Upload className="w-3.5 h-3.5" />
                Add SME files (max 5)
              </label>
              {smeFiles && smeFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {smeFiles.map((f, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-xs bg-gray-50 px-2 py-1 rounded">
                      <span className="flex items-center gap-1.5 text-gray-700 truncate">
                        <FileText className="w-3 h-3 flex-shrink-0" />
                        {f.name}
                        <span className="text-gray-400">({Math.round(f.size / 1024)} KB)</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSmeFile(i)}
                        className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                        aria-label={`Remove ${f.name}`}
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Markdown Table Renderer ──────────────────────────────────────────── */

const renderMarkdownTable = (md: string) => {
  if (!md) return <p className="text-sm text-gray-400 italic">No preview available</p>;
  const lines = md.split('\n').filter(l => l.trim() && !/^\|[\s\-|:]+\|$/.test(l.trim()));
  if (lines.length === 0) return <p className="text-sm text-gray-400 italic">No data</p>;
  const parseRow = (line: string) => line.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 scrollbar-thin">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-900 text-white">
            {headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, ci) => <td key={ci} className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─── DataCleaningResults ──────────────────────────────────────────────── */

export const DataCleaningResults: React.FC<DataCleaningResultsProps> = ({
  result, activeTab, setActiveTab, onSendToSmartAnalyst, onSendToCouncil, isExpanded, onToggleExpand
}) => {
  const [showQuestions, setShowQuestions] = React.useState(true);

  const handleDownload = async () => {
    if (!result.cleanedFileUrl) { toast.error('Cleaned file not available yet'); return; }
    try {
      const response = await api.get(result.cleanedFileUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = result.cleanedFileName || 'cleaned_data.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded cleaned dataset');
    } catch {
      toast.error('Failed to download');
    }
  };

  const qualityBefore = result.stats?.qualityScoreBefore || 0;
  const qualityAfter = result.stats?.qualityScoreAfter || 0;
  const qualityImprovement = qualityAfter - qualityBefore;
  const beforeColor = qualityBefore >= 70 ? '#10b981' : qualityBefore >= 40 ? '#f59e0b' : '#ef4444';
  const afterColor = qualityAfter >= 70 ? '#10b981' : qualityAfter >= 40 ? '#f59e0b' : '#ef4444';

  const canonicalCount = result.canonicalMapping?.length || 0;
  const insightCount = result.smeDerivedInsights?.length || 0;
  const hasSme = canonicalCount > 0 || insightCount > 0 || !!result.smeNotesProvided || (result.smeFilesUsed && result.smeFilesUsed.length > 0);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'issues', label: `Issues (${result.issuesFound?.length || 0})` },
    ...(result.stakeholderQuestions?.length ? [{ id: 'questions', label: `Questions (${result.stakeholderQuestions.length})` }] : []),
    ...(hasSme ? [{ id: 'sme', label: `SME Enrichment${canonicalCount ? ` (${canonicalCount})` : ''}` }] : []),
    { id: 'changelog', label: 'Change Log' },
    { id: 'preview', label: 'Data Preview' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-emerald-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
              <CheckCircle className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <span className="text-sm font-bold text-gray-800">Data Cleaning Complete</span>
              {result.stats?.totalRows && (
                <p className="text-xs text-gray-500">
                  {result.stats.totalRows.toLocaleString()} rows &rarr; {(result.stats.rowsAfterCleaning || result.stats.totalRows).toLocaleString()} rows &middot; {result.originalFileNames?.join(', ')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {result.cleanedFileUrl && (
              <button onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            )}
            <button onClick={onSendToSmartAnalyst} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors shadow-sm">
              <ArrowRight className="w-3.5 h-3.5" /> Smart Data Analyst
            </button>
            <button onClick={onSendToCouncil} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200">
              <ExternalLink className="w-3.5 h-3.5" /> AI Council
            </button>
            <button
              onClick={onToggleExpand}
              className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors ml-1"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-1 mt-3">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-500 hover:text-gray-700 border border-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────────── */}
      <div className={`p-5 overflow-y-auto scrollbar-thin ${isExpanded ? 'h-[calc(100vh-160px)]' : 'max-h-[560px]'}`}>

        {/* ── Overview Tab ──────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Quality Score Cards */}
            <div className="flex items-center justify-center gap-8 py-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-100">
              <QualityRing score={qualityBefore} label="Before Cleaning" color={beforeColor} />
              <div className="flex flex-col items-center gap-1">
                <TrendingUp className="w-5 h-5 text-teal-500" />
                <span className={`text-lg font-bold ${qualityImprovement > 0 ? 'text-teal-600' : 'text-gray-400'}`}>
                  {qualityImprovement > 0 ? '+' : ''}{qualityImprovement}%
                </span>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Improvement</span>
              </div>
              <QualityRing score={qualityAfter} label="After Cleaning" color={afterColor} />
            </div>

            {/* Stats Grid */}
            {result.stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {result.stats.duplicatesRemoved != null && (
                  <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
                    <div className="text-2xl font-bold text-red-600">{result.stats.duplicatesRemoved}</div>
                    <div className="text-[10px] text-red-500 mt-1 font-semibold uppercase tracking-wider">Duplicates Removed</div>
                  </div>
                )}
                {result.stats.nullsFilled != null && (
                  <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                    <div className="text-2xl font-bold text-amber-600">{result.stats.nullsFilled}</div>
                    <div className="text-[10px] text-amber-500 mt-1 font-semibold uppercase tracking-wider">Nulls Filled</div>
                  </div>
                )}
                {result.stats.typesFixed != null && (
                  <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                    <div className="text-2xl font-bold text-blue-600">{result.stats.typesFixed}</div>
                    <div className="text-[10px] text-blue-500 mt-1 font-semibold uppercase tracking-wider">Types Fixed</div>
                  </div>
                )}
                {result.stats.formatsStandardized != null && (
                  <div className="bg-teal-50 rounded-xl p-3 text-center border border-teal-100">
                    <div className="text-2xl font-bold text-teal-600">{result.stats.formatsStandardized}</div>
                    <div className="text-[10px] text-teal-500 mt-1 font-semibold uppercase tracking-wider">Formats Fixed</div>
                  </div>
                )}
              </div>
            )}

            {/* Executive Summary */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Executive Summary</p>
              <div className="prose prose-sm max-w-none text-gray-700">
                {result.summary?.split('\n').map((line: string, i: number) => {
                  if (line.startsWith('# ') && !line.startsWith('## ')) return <h2 key={i} className="text-base font-bold text-gray-900 mt-3 mb-1">{renderInline(line.replace(/^# /, ''))}</h2>;
                  if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-bold text-gray-800 mt-3 mb-1">{renderInline(line.replace('## ', ''))}</h3>;
                  if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-semibold text-gray-700 mt-2 mb-1">{renderInline(line.replace('### ', ''))}</h4>;
                  if (/^\d+\.\s/.test(line)) return <p key={i} className="text-sm text-gray-600 ml-4 mb-1">{renderInline(line)}</p>;
                  if (line.startsWith('- ')) return <p key={i} className="text-sm text-gray-600 ml-4 mb-1">• {renderInline(line.replace('- ', ''))}</p>;
                  if (line.trim()) return <p key={i} className="text-sm text-gray-600 mb-1">{renderInline(line)}</p>;
                  return null;
                })}
              </div>
            </div>

            {/* Stakeholder Questions Preview (if any) */}
            {result.stakeholderQuestions && result.stakeholderQuestions.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <button
                  onClick={() => setShowQuestions(v => !v)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-bold text-amber-800">
                      {result.stakeholderQuestions.length} Questions for Stakeholders
                    </span>
                    <span className="text-xs text-amber-600">— Items that require human review</span>
                  </div>
                  {showQuestions ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                </button>
                {showQuestions && (
                  <div className="mt-3 space-y-2">
                    {result.stakeholderQuestions.slice(0, 3).map((q, i) => (
                      <div key={i} className="flex items-start gap-2 bg-white rounded-lg p-2.5 border border-amber-100">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-200 text-amber-800 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{q.question}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{q.context}</p>
                        </div>
                      </div>
                    ))}
                    {result.stakeholderQuestions.length > 3 && (
                      <button
                        onClick={() => setActiveTab('questions')}
                        className="text-xs font-semibold text-amber-700 hover:text-amber-800 ml-7"
                      >
                        View all {result.stakeholderQuestions.length} questions →
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Issues Tab ────────────────────────────────────────────────── */}
        {activeTab === 'issues' && (
          <div>
            {(!result.issuesFound || result.issuesFound.length === 0) ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" /> No data quality issues detected
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 scrollbar-thin">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-3 py-2.5 text-left font-semibold">Severity</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Type</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Column</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Description</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Count</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Action</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.issuesFound.map((issue: CleaningIssue, i: number) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${SEVERITY_COLOR[issue.severity] || 'bg-gray-100 text-gray-600'}`}>
                            {issue.severity?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 capitalize">{issue.type?.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 font-mono text-gray-700 text-[11px]">{issue.column}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[220px]">{issue.description}</td>
                        <td className="px-3 py-2 text-center font-bold text-gray-700">{issue.count}</td>
                        <td className="px-3 py-2 text-gray-600">{issue.action}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            issue.status === 'fixed' ? 'bg-green-100 text-green-700' :
                            issue.status === 'needs_review' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {issue.status === 'fixed' ? '✓ Fixed' : issue.status === 'needs_review' ? '⚠ Review' : issue.status === 'cannot_fix' ? '✗ Manual' : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Stakeholder Questions Tab ──────────────────────────────────── */}
        {activeTab === 'questions' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-amber-600" />
              <p className="text-sm text-gray-600">
                These questions require <strong>human domain knowledge</strong> — the automated cleaning process cannot resolve them.
                Share these with relevant stakeholders to improve data quality further.
              </p>
            </div>
            {(result.stakeholderQuestions || []).map((q: StakeholderQuestion, i: number) => (
              <div key={i} className={`rounded-xl p-4 border ${PRIORITY_COLOR[q.priority] || 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/80 text-sm font-bold flex items-center justify-center border">{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{q.question}</p>
                    <p className="text-xs text-gray-500 mt-1">{q.context}</p>
                    {q.affectedColumns?.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Columns:</span>
                        {q.affectedColumns.map((col, ci) => (
                          <span key={ci} className="px-1.5 py-0.5 bg-white/60 rounded text-[10px] font-mono text-gray-600 border">{col}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                    q.priority === 'high' ? 'bg-red-200 text-red-800' : q.priority === 'medium' ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'
                  }`}>{q.priority}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SME Enrichment Tab ─────────────────────────────────────────── */}
        {activeTab === 'sme' && (
          <div className="space-y-4">
            {(result.smeFilesUsed && result.smeFilesUsed.length > 0) || result.smeNotesProvided ? (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-teal-800 mb-1">
                  <Users className="w-3.5 h-3.5" />
                  SME context was provided
                </div>
                <div className="text-xs text-teal-700">
                  {result.smeNotesProvided && <span>• Inline notes</span>}
                  {result.smeFilesUsed && result.smeFilesUsed.length > 0 && (
                    <span className="ml-2">
                      • {result.smeFilesUsed.length} file(s): {result.smeFilesUsed.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            ) : null}

            {result.smeDerivedInsights && result.smeDerivedInsights.length > 0 && (
              <div>
                <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Insights from SME context
                </h5>
                <ul className="space-y-2">
                  {result.smeDerivedInsights.map((ins, i) => (
                    <li key={i} className="text-sm text-gray-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                      {ins}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.canonicalMapping && result.canonicalMapping.length > 0 ? (
              <div>
                <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-teal-600" />
                  Proposed canonical name mapping
                </h5>
                <div className="overflow-x-auto rounded-lg border border-gray-200 scrollbar-thin">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-900 text-white">
                        <th className="px-3 py-2.5 text-left font-semibold">Original</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Canonical</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Column</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Confidence</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.canonicalMapping.map((m, i) => {
                        const pct = Math.round((m.confidence || 0) * 100);
                        const pctColor = pct >= 75 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                        return (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 font-mono text-gray-700">{m.original}</td>
                            <td className="px-3 py-2 font-mono text-teal-700 font-semibold">{m.canonical}</td>
                            <td className="px-3 py-2 text-gray-500">{m.sourceColumn || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${pctColor}`}>
                                {pct}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{m.reason}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  These mappings are proposals derived from your SME context — review them before applying.
                </p>
              </div>
            ) : (
              (!result.smeDerivedInsights || result.smeDerivedInsights.length === 0) && (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  No canonical name mappings were proposed. The SME context either didn't surface any
                  naming ambiguities, or none were provided.
                </div>
              )
            )}
          </div>
        )}

        {/* ── Change Log Tab ─────────────────────────────────────────────── */}
        {activeTab === 'changelog' && (
          <div className="space-y-3">
            {result.changeLog && result.changeLog.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200 scrollbar-thin">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-900 text-white">
                        <th className="px-3 py-2.5 text-left font-semibold w-8">#</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Operation</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Details</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Rows Affected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.changeLog.map((entry: ChangeLogEntry, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 text-gray-400 font-mono">{i + 1}</td>
                          <td className="px-3 py-2 font-semibold text-gray-700">{entry.operation}</td>
                          <td className="px-3 py-2 text-gray-600">{entry.details}</td>
                          <td className="px-3 py-2 text-right font-bold text-gray-700">{entry.rowsAffected?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Raw log */}
                {result.cleaningLog && (
                  <details className="group">
                    <summary className="text-xs font-semibold text-gray-400 cursor-pointer hover:text-gray-600 flex items-center gap-1">
                      <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" /> Raw Python Log
                    </summary>
                    <div className="bg-gray-900 rounded-xl p-4 mt-2 max-h-64 overflow-y-auto scrollbar-thin">
                      <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{result.cleaningLog}</pre>
                    </div>
                  </details>
                )}
              </>
            ) : (
              /* Fallback: raw log only */
              <div className="bg-gray-900 rounded-xl p-4 max-h-96 overflow-y-auto scrollbar-thin">
                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                  {result.cleaningLog || 'No cleaning log available'}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* ── Data Preview Tab ───────────────────────────────────────────── */}
        {activeTab === 'preview' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" /> Before Cleaning
              </h4>
              {renderMarkdownTable(result.previewBefore)}
            </div>
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200">
                <ArrowRight className="w-4 h-4 text-teal-600" />
                <span className="text-xs font-semibold text-teal-700">Cleaned</span>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-teal-500" /> After Cleaning
              </h4>
              {renderMarkdownTable(result.previewAfter)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
